import { TRenewVisaEmployeeApplicationDocument } from "@/api/entities/renew-visa-employee-application-document.entity";
import { TRenewVisaUploadFileDocument } from "@/api/entities/renew-visa-upload-file-document.entity";
import {
  createRenewVisaUploadFileDocumentApi,
  updateRenewVisaUploadFileDocumentApi,
  useGetRenewVisaDocumentInfo,
  useGetRenewVisaEmployeeApplicationDocumentInfo,
} from "@/api/services/main/renew-visa";
import { IconCheck } from "@/assets/icons";
import { Checkbox } from "@/components/elements/Checkbox";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { FILE_EXISTED } from "@/constants/file";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { FileBoxInput } from "@/features/renew-visa/components/applicationStep/uploadApplication/FileBoxInput";
import { FileInfo } from "@/features/renew-visa/components/applicationStep/uploadApplication/FileInfo";
import { GroupSixFileBoxInput } from "@/features/renew-visa/components/applicationStep/uploadApplication/GroupSixFileBoxInput";
import { UploadFileBtnActions } from "@/features/renew-visa/components/applicationStep/uploadApplication/UploadFileBtnActions";
import { RENEW_VISA_UPLOAD_APPLICATION_FILE } from "@/features/renew-visa/constants/uploadApplicationFile";
import { useGetRenewVisaUploadFileSchema } from "@/features/renew-visa/hook/useGetRenewVisaUploadFileSchema";
import { useGetUploadFileListWithFilterCondition } from "@/features/renew-visa/hook/useGetUploadFileListWithFilterCondition";
import { useMergeRenewVisaFile } from "@/features/renew-visa/hook/useMergeRenewVisaFile";
import { TRenewVisaUploadFileForm } from "@/features/renew-visa/types/renewVisaUploadFileForm";
import { convertUploadApplicationFormDataToReqDto } from "@/features/renew-visa/utils/convertUploadApplicationFormDataToReqDto";
import { AppRoutes } from "@/routes/config";
import { background, divider, state, text } from "@/theme/colors";
import { RENEW_VISA_DOCUMENT_STATUS } from "@/types/enum";
import { setErrorForm, showError } from "@/utils/error";
import { toNumber } from "@/utils/number";
import { getFormValues, uniqueArray } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Stack, styled, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, Fragment, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

type TUploadApplicationFormProps = {
  status?: string;
  previousValues: TRenewVisaUploadFileForm | undefined;
  isExistUploadFileData?: boolean;
  readonly?: boolean;
  isEditable?: boolean;
  setIsEditable?: (bool: boolean) => void;
};

export const UploadApplicationForm: FC<TUploadApplicationFormProps> = ({
  status,
  isExistUploadFileData,
  readonly,
  previousValues,
  isEditable,
  setIsEditable,
}) => {
  const { employeeRenewVisaDocumentGroupId, renewVisaDocumentId, documentStepId } = useParams();

  const { uploadFileListWithFilterCondition, filterConditionArrayKeys, conditions } = useGetUploadFileListWithFilterCondition();

  const { data: renewVisaDocument } = useGetRenewVisaDocumentInfo();

  const { getFilesGroupByTable } = useMergeRenewVisaFile();

  const [isUploadingFileKey, setIsUploadingFileKey] = useState<string>();

  const navigate = useNavigate();

  const { renewVisaUploadFileSchema, renewVisaUploadFileDraftSchema } = useGetRenewVisaUploadFileSchema();
  const queryClient = useQueryClient();
  const methods = useForm<TRenewVisaUploadFileForm>({
    defaultValues: previousValues,
    resolver: yupResolver(renewVisaUploadFileSchema),
  });

  const { data: employeeApplicationData } = useGetRenewVisaEmployeeApplicationDocumentInfo();

  const { control } = methods;

  const saveDataForm = async (data: TRenewVisaUploadFileForm, isDraft: boolean) => {
    if (!renewVisaDocumentId) return;
    const { isShowTable1, isShowTable2And1, isShowTable2And2, isShowTable2And3, isShowTable3And1, isShowTable3And2, isShowTable3And3 } =
      await getFilesGroupByTable(
        !!employeeApplicationData ? employeeApplicationData : ({ employee: renewVisaDocument?.employee } as TRenewVisaEmployeeApplicationDocument),
        data as TRenewVisaUploadFileDocument,
      );

    const params = {
      ...data,
      residenceApplicationsForEmployeeTable1FilePath: isShowTable1 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable2And1FilePath: isShowTable2And1 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable2And2FilePath: isShowTable2And2 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable2And3FilePath: isShowTable2And3 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable3And1FilePath: isShowTable3And1 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable3And2FilePath: isShowTable3And2 ? FILE_EXISTED : null,
      residenceApplicationsForEmployeeTable3And3FilePath: isShowTable3And3 ? FILE_EXISTED : null,
    };

    if (!renewVisaDocumentId || !documentStepId) return;
    try {
      if (isExistUploadFileData) {
        const rs = await updateRenewVisaUploadFileDocumentApi(renewVisaDocumentId, documentStepId, {
          ...convertUploadApplicationFormDataToReqDto(params, filterConditionArrayKeys, isDraft),
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      } else {
        const rs = await createRenewVisaUploadFileDocumentApi(renewVisaDocumentId, documentStepId, {
          ...convertUploadApplicationFormDataToReqDto(params, filterConditionArrayKeys, isDraft),
        });
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      }

      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_DOCUMENT_CREATION_GROUP] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_UPLOAD_FILE] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RENEW_VISA_DOCUMENTS.FETCH_GROUP_DETAIL] });
      if (isDraft) {
        methods.unregister();
      }
      navigate(getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId }));
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onSubmit = async (data: TRenewVisaUploadFileForm) => {
    if (readonly) {
      navigate(getDynamicRoute(AppRoutes.renewVisaPresent, { employeeRenewVisaDocumentGroupId }));
      return;
    }
    await saveDataForm(data, false);
  };

  const saveDraft = async () => {
    try {
      const data = methods.getValues();
      const values = await getFormValues(data, renewVisaUploadFileDraftSchema);
      await saveDataForm(values as TRenewVisaUploadFileForm, true);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form onSubmit={onSubmit} methods={methods} schema={renewVisaUploadFileSchema}>
      {uploadFileListWithFilterCondition
        // If select one of A-E and is in progress and is group 6, then do not show this group
        .filter((item) => {
          if (
            conditions.length > 0 &&
            status === RENEW_VISA_DOCUMENT_STATUS.IN_PROGRESS &&
            item.label === RENEW_VISA_UPLOAD_APPLICATION_FILE.GROUP_SIX.label
          ) {
            return false;
          }
          return true;
        })
        .map((item, index) => {
          const isGroupSix = item.label === "提出書類の一覧表";

          if (item.value.length === 0) return <></>;
          return (
            <Stack key={index} sx={{ backgroundColor: background.white, border: "1px solid #E6E7E8", borderRadius: "8px", marginTop: "16px" }}>
              <CardHeader
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                pl={3}
                gap={10}
                height="56px"
                sx={{ backgroundColor: background.secondary, borderBottom: "1px solid #E6E7E8" }}
              >
                <Typography variant="body14Bold">{item.label}</Typography>
              </CardHeader>
              <Box px={3}>
                <Typography component="h4" variant="body14" mt="16px" mb="24px" whiteSpace="pre-line">
                  {item.subLabel}
                </Typography>
                <Stack gap={2} pb={2}>
                  {item.value.map((valueItem, index) => (
                    <Fragment key={index}>
                      {!valueItem.children && (
                        <>
                          {isGroupSix ? (
                            <GroupSixFileBoxInput
                              control={control}
                              title={valueItem.uploadScreenLabel}
                              name={valueItem.key}
                              readonly={readonly || !isEditable}
                              filePath={methods.watch(valueItem.key)?.toString()}
                              required={valueItem.required}
                            />
                          ) : (
                            <Stack
                              flexDirection="row"
                              justifyContent="space-between"
                              alignItems="center"
                              gap={10}
                              py={2}
                              px={3}
                              sx={{ border: "1px solid #E6E7E8", borderRadius: "8px" }}
                            >
                              <Box width={680}>
                                <FileInfo
                                  required={valueItem.required}
                                  titleVariant="sub16Semi"
                                  title={valueItem.uploadScreenLabel}
                                  isUploaded={
                                    (valueItem.multiple
                                      ? toNumber((methods.watch(valueItem.key) as Array<string>)?.length) > 0
                                      : !!methods.watch(valueItem.key)) ||
                                    (!!valueItem.isExistKey && !!methods.watch(valueItem.isExistKey))
                                  }
                                  subTitle={valueItem.subUploadScreenLabel}
                                  tooltipContent={valueItem.tooltipContent}
                                />
                              </Box>
                              <Stack justifyContent="flex-end" alignItems="flex-end">
                                <Box width={198}>
                                  <FormField name={valueItem.key} control={control} errorTextAlign="right">
                                    <FileBoxInput
                                      readonly={readonly || !isEditable}
                                      disabled={
                                        (!!valueItem.isExistKey && !!methods.watch(valueItem.isExistKey)) ||
                                        (!!valueItem.inputKey && !!methods.watch(valueItem.inputKey))
                                      }
                                      multiple={valueItem.multiple}
                                      maxFiles={valueItem.maxFiles}
                                      setIsUploadingFileKey={setIsUploadingFileKey}
                                    />
                                  </FormField>
                                </Box>
                                {valueItem.inputKey && (
                                  <Box width={280}>
                                    <FormField sx={{ mt: 1 }} control={control} name={valueItem.inputKey}>
                                      <Input
                                        readonly={readonly || !isEditable}
                                        disabled={
                                          (valueItem.multiple
                                            ? toNumber((methods.watch(valueItem.key) as Array<string>)?.length) > 0
                                            : !!methods.watch(valueItem.key)) || isUploadingFileKey === valueItem.key
                                        }
                                      />
                                    </FormField>
                                  </Box>
                                )}
                                {valueItem.isExistKey && (
                                  <FormField name={valueItem.isExistKey} control={control}>
                                    <Checkbox
                                      checked={methods.getValues(valueItem.isExistKey) || false}
                                      disabled={
                                        readonly ||
                                        !isEditable ||
                                        (valueItem.multiple
                                          ? toNumber((methods.watch(valueItem.key) as Array<string>)?.length) > 0
                                          : !!methods.watch(valueItem.key))
                                      }
                                      labelColor={
                                        (
                                          valueItem.multiple
                                            ? toNumber((methods.watch(valueItem.key) as Array<string>)?.length) > 0
                                            : !!methods.watch(valueItem.key)
                                        )
                                          ? text.disable
                                          : undefined
                                      }
                                      sx={{ ml: 0.5 }}
                                      label="添付対応済"
                                    />
                                  </FormField>
                                )}
                              </Stack>
                            </Stack>
                          )}
                        </>
                      )}
                      {valueItem.children && (
                        <Stack sx={{ border: "1px solid #E6E7E8", borderRadius: "8px" }}>
                          <Stack direction="row" alignItems="center" gap={1} px={3} mt={2}>
                            <Stack
                              alignItems="center"
                              justifyContent="center"
                              width={16}
                              height={16}
                              borderRadius={16}
                              bgcolor={
                                valueItem.children.some((childrenItem) => !!methods.watch(childrenItem.key)) ? state.success_1 : divider.middle
                              }
                            >
                              <IconCheck color={background.white} />
                            </Stack>
                            <Typography component="p" variant="sub16Semi" whiteSpace="pre-line">
                              {valueItem.uploadScreenLabel}
                            </Typography>
                            {valueItem.required && <Typography color={text.error}>*</Typography>}
                          </Stack>
                          <Stack ml={3} gap={1}>
                            {valueItem.children.map((childItem, index) => {
                              const childrenKeys = valueItem.children ? valueItem.children.map((item) => item.key).filter((item) => !!item) : [];
                              const childrenInputKeys = valueItem.children
                                ? valueItem.children.map((item) => item.inputKey).filter((item) => !!item)
                                : [];
                              return (
                                <Stack flexDirection="row" justifyContent="space-between" alignItems="center" gap={10} py={1} px={3}>
                                  <Box width={680}>
                                    <FileInfo
                                      titleVariant="sub16Semi"
                                      title={childItem.uploadScreenLabel}
                                      showCheckIcon={false}
                                      isUploaded={!!methods.watch(childItem.key)}
                                      tooltipContent={childItem.tooltipContent}
                                    />
                                  </Box>
                                  <Stack justifyContent="flex-end" alignItems="flex-end">
                                    <Box width={198}>
                                      <FormField key={index} name={childItem.key} control={control} errorTextAlign="right">
                                        <FileBoxInput
                                          setIsUploadingFileKey={setIsUploadingFileKey}
                                          disabled={!!childItem.inputKey && !!methods.watch(childItem.inputKey)}
                                          triggerArrKeys={uniqueArray([...childrenKeys, ...childrenInputKeys] as (keyof TRenewVisaUploadFileForm)[])}
                                          readonly={readonly || !isEditable}
                                        />
                                      </FormField>
                                    </Box>
                                    {childItem.inputKey && (
                                      <Box width={280}>
                                        <FormField sx={{ mt: 1 }} control={control} name={childItem.inputKey}>
                                          <Input
                                            readonly={readonly || !isEditable}
                                            disabled={!!methods.watch(childItem.key) || isUploadingFileKey === valueItem.key}
                                          />
                                        </FormField>
                                      </Box>
                                    )}
                                  </Stack>
                                </Stack>
                              );
                            })}
                          </Stack>
                        </Stack>
                      )}
                    </Fragment>
                  ))}
                </Stack>
              </Box>
            </Stack>
          );
        })}
      <UploadFileBtnActions saveDraft={saveDraft} readonly={readonly} isEditable={!!isEditable} setIsEditable={(v) => setIsEditable?.(v)} />
    </Form>
  );
};

const CardHeader = styled(Stack)`
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;
